import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Paper, SelectChangeEvent, Skeleton, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUserPlanStatusWithTransactionLimit } from '../../hooks/useUserPlanStatusWithTransactionLimit';
import { TransactionIssueType } from '../../services/transaction.types';
import PageLayout from '../common/PageLayout';
import { useTransactionNavigation } from '../transactions/hooks/useTransactionNavigation';
import CurrencyTable from './components/CurrencyTable';
import DownloadReportCard from './components/DownloadReportCard';
import TaxReportWarningBanner from './components/TaxReportWarningBanner';
import TaxSettingsCard from './components/TaxSettingsCard';
import TaxSummaryBox from './components/TaxSummaryBox';
import TransactionInfoIcon from './components/TransactionInfoIcon';
import TransactionLimitAlert from './components/TransactionLimitAlert';
import TransactionProgress from './components/TransactionProgress';
import YearSelector from './components/YearSelector';
import { useTaxReportQueries } from './hooks/useTaxReportsQueries';
import TaxReportSummary from './TaxReportSummary';
import { TaxReportFormat } from './types';

const TaxReportPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { navigateToTransactions } = useTransactionNavigation();
    const showBottomTable = false;

    const {
        taxReports,
        selectedYear,
        setSelectedYear,
        taxPersonalData,
        canLoadDetails,
        reportSummary,
        transactionStats,
        yearOptions,
        downloadPdf: handleDownloadPdf,
        downloadExcel: handleDownloadExcel,
        isLoading,
        isDeleting,
    } = useTaxReportQueries();

    const { getTransactionLimit, hasWeDoEverythingAddon } = useUserPlanStatusWithTransactionLimit();

    const planLimit = getTransactionLimit(selectedYear);

    const shouldHideTransactionLimits = hasWeDoEverythingAddon(selectedYear);

    const costBasisSavings = transactionStats?.costBasisMissingTotalPrice || 0;
    const taxableAssets = transactionStats?.taxableAssets || [];
    const taxableTransactionCount = transactionStats?.taxableTransactionCount || 0;

    const [selectedDownloadType] = useState(TaxReportFormat.PDF);

    const handleYearChange = (event: SelectChangeEvent) => {
        setSelectedYear(event.target.value);
    };

    const handleNavigateToPlans = () => {
        navigate(`/plans?year=${selectedYear}&transactions=${transactionStats?.totalTransactions}`);
    };

    const handleNavigateToTransactions = (selectedFlag: TransactionIssueType) => {
        navigateToTransactions({
            flags: [selectedFlag],
            dates: {
                start: `${selectedYear}-01-01`,
                end: `${selectedYear}-12-31`,
            },
        });
    };

    const handleDownload = (format: TaxReportFormat = selectedDownloadType) => {
        const now = new Date();
        const formattedDate = `${String(now.getDate()).padStart(2, '0')}.${String(now.getMonth() + 1).padStart(2, '0')}.${now.getFullYear()}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
        const fileName = `coinfox - ${t('taxReports.fileName')} ${formattedDate}`;

        switch (format) {
            case TaxReportFormat.PDF:
                handleDownloadPdf(`${fileName}.pdf`);
                break;
            case TaxReportFormat.EXCEL:
                handleDownloadExcel(`${fileName}.xlsx`);
                break;
            default:
                break;
        }
    };

    if (isLoading || isDeleting) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    width: '100%',
                    p: 3,
                }}
            >
                <Skeleton variant="text" width={210} height={40} />
                <Skeleton variant="rectangular" width="100%" height={118} />
                <Skeleton variant="rectangular" width="100%" height={118} />
            </Box>
        );
    }

    if (taxReports.length === 0) {
        return (
            <PageLayout title={t('taxReports.title')}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%',
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            bgcolor: 'background.paper',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            p: 4,
                            width: '100%',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 6,
                                textAlign: 'center',
                            }}
                        >
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                {t('plans.noYearsAvailable.title')}
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', maxWidth: 450 }}>
                                {t('plans.noYearsAvailable.description')}
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={() => navigate('/integrations')}
                                sx={{ fontWeight: 'bold' }}
                            >
                                {t('plans.noYearsAvailable.cta')}
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            </PageLayout>
        );
    }

    return (
        <PageLayout title={t('taxReports.title')}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    width: '100%',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 2,
                        width: '100%',
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            bgcolor: (theme) => theme.palette.background.paper,
                            borderRadius: 1,
                            flex: 1,
                            minWidth: 0,
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            px: 4,
                            py: 4, // Increased from py: 3
                        }}
                    >
                        {/* Top area with year selector and transaction count */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', lg: 'row' },
                                justifyContent: 'space-between',
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                gap: { xs: 2, sm: 0 },
                                mb: 3, // Increased from mb: 2
                            }}
                        >
                            <YearSelector
                                selectedYear={selectedYear}
                                yearOptions={yearOptions}
                                onYearChange={handleYearChange}
                            />

                            {/* Show either transaction progress or just the info icon - hide if user has "We Do Everything" addon with access to this year */}
                            {!shouldHideTransactionLimits && (
                                <>
                                    {canLoadDetails ? (
                                        <TransactionProgress
                                            allTimeTransactionCount={transactionStats?.allTimeTotalTransactions || 0}
                                            transactionCount={transactionStats?.totalTransactions || 0}
                                            planLimit={planLimit}
                                            selectedYear={selectedYear}
                                        />
                                    ) : (
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                            <TransactionInfoIcon
                                                allTimeTransactionCount={
                                                    transactionStats?.allTimeTotalTransactions || 0
                                                }
                                                transactionCount={transactionStats?.totalTransactions || 0}
                                                planLimit={planLimit}
                                                selectedYear={selectedYear}
                                            />
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>

                        {/* Transaction limit exceeded alert - hide if user has "We Do Everything" addon with access to this year */}
                        {!canLoadDetails && !shouldHideTransactionLimits && (
                            <TransactionLimitAlert
                                transactionCount={transactionStats?.totalTransactions || 0}
                                planLimit={planLimit}
                                onNavigateToPlans={handleNavigateToPlans}
                                taxableAssets={taxableAssets}
                                issuesTransactionCount={taxableTransactionCount}
                            />
                        )}

                        {/* Combined tax summary box with both taxable amount and savings potential */}
                        <TaxSummaryBox
                            data={reportSummary || []}
                            isPaid={canLoadDetails}
                            savingsPotential={costBasisSavings}
                            onNavigateToTransactions={handleNavigateToTransactions}
                        />

                        <TaxReportSummary data={reportSummary || []} isPaid={canLoadDetails} />
                    </Paper>

                    <Stack
                        spacing={3}
                        sx={{
                            width: { xs: '100%', md: 300 },
                            flexShrink: 0,
                        }}
                    >
                        {/* Warning Banner */}
                        <TaxReportWarningBanner />

                        {/* 1. Tax Information section */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                bgcolor: (theme) => theme.palette.background.paper,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            }}
                        >
                            <TaxSettingsCard />
                        </Paper>

                        {/* 3. Download Report card */}
                        <DownloadReportCard
                            onDownloadPdf={() => handleDownload(TaxReportFormat.PDF)}
                            onDownloadExcel={() => handleDownload(TaxReportFormat.EXCEL)}
                            isPlanPurchased={!!(taxPersonalData && canLoadDetails)}
                        />
                    </Stack>
                </Box>

                {!!(taxPersonalData && canLoadDetails && showBottomTable) && <CurrencyTable data={[]} />}
            </Box>
        </PageLayout>
    );
};

export default TaxReportPage;
